/* @refresh reload */
import { render } from "solid-js/web";

import { App } from "@/app";
import { isTauriRuntime } from "@/lib/platform";
import { hydrateSecureTokens, primeActiveNativeToken } from "@/lib/secure-token";
import "@/styles/global.css";

// PWA: auto-update de UNA sola recarga. El `sw.js` (workbox) ya hace `skipWaiting()` al recibir
// `{type:"SKIP_WAITING"}`; aquí, al detectar un SW nuevo INSTALADO habiendo ya un controller (=es
// una actualización, no la primera instalación), se lo pedimos → toma control → `controllerchange`
// → recargamos UNA vez. Así basta un refresh del usuario (antes el SW nuevo quedaba `waiting`).
// Dependency-free a propósito (evita workbox-window). Para volverlo TOTALMENTE automático (recargar
// pestañas inactivas sin acción del usuario) ver infra/PWA_UPDATES.md. En Tauri no hay SW → no-op.
// Guard: en algunos contextos (p. ej. Playwright con `serviceWorkers: "block"`)
// la clave `serviceWorker` existe en `navigator` pero su valor es `undefined`,
// así que `"serviceWorker" in navigator` no basta: hay que comprobar el objeto
// antes de tocar `.addEventListener` (si no, `pageerror` en el boot).
if ("serviceWorker" in navigator && navigator.serviceWorker) {
  const swContainer = navigator.serviceWorker;
  let reloading = false;
  swContainer.addEventListener("controllerchange", () => {
    if (reloading) {
      return;
    }
    reloading = true;
    window.location.reload();
  });
  window.addEventListener("load", () => {
    void swContainer.register("/sw.js", { scope: "/" }).then((reg) => {
      // Con `serviceWorkers: "block"` (Playwright) `register()` resuelve a
      // `undefined`; sin esta guarda `reg.addEventListener` lanza un pageerror.
      if (!reg) {
        return undefined;
      }
      reg.addEventListener("updatefound", () => {
        const next = reg.installing;
        if (!next) {
          return;
        }
        next.addEventListener("statechange", () => {
          // Solo en ACTUALIZACIÓN (ya hay controller); en la 1ª instalación no recargamos.
          if (next.state === "installed" && swContainer.controller) {
            // `next` es un ServiceWorker, no una Window: su `postMessage(message, transfer?)`
            // NO acepta targetOrigin (ese 2º arg es la transfer-list). La regla, pensada para
            // Window.postMessage, es un falso positivo aquí.
            // oxlint-disable-next-line unicorn/require-post-message-target-origin
            next.postMessage({ type: "SKIP_WAITING" });
          }
        });
      });
      return undefined;
    });
  });
}

const root = document.querySelector("#root");

if (!root) {
  throw new Error("Root element #root not found in index.html");
}
const rootEl: Element = root;

function mountApp(): void {
  render(() => <App />, rootEl);

  // Tell the index.html boot watchdog the app mounted: cancels the self-heal
  // reload and clears its one-shot guard (so a future stale-deploy can recover).
  (window as typeof window & { __appBootOk?: () => void }).__appBootOk?.();
}

if (isTauriRuntime()) {
  // Tauri DESKTOP: load the Bearer token(s) from the OS keychain into the
  // synchronous in-memory cache BEFORE the app mounts, so the very first
  // authenticated request resolves its token (keychain reads are async; the
  // hot read-path is sync). Best-effort — never block boot on a keychain error.
  try {
    await hydrateSecureTokens();
    // Phase 2: prime the RUST-held active token from the store so the
    // `api_request` proxy can inject the Bearer on the first authenticated
    // request. The token stays in Rust — JS never receives it.
    await primeActiveNativeToken();
  } catch (err) {
    console.warn("[secure-token] hydration failed, continuing:", err);
  }
}
// Web (cookie-BFF) renders immediately; Tauri renders once the cache is warm.
mountApp();

// Dismiss the first-paint loader baked into index.html once the app shell has
// mounted. We wait two animation frames so the freshly-rendered shell has
// actually painted before we fade the loader, avoiding a flash-to-blank.
function dismissAppLoader() {
  const loader = document.querySelector("#app-loader");
  if (!loader) {
    return;
  }
  loader.classList.add("app-loader--hidden");
  const LOADER_FADE_MS = 300;
  setTimeout(() => {
    loader.remove();
  }, LOADER_FADE_MS);
}
requestAnimationFrame(() => {
  requestAnimationFrame(dismissAppLoader);
});
// cache-warm probe 1781180306
// cache probe web-only 1781181006
// ci cache probe 2 1781182938
